import React, { createContext, useContext, useEffect, useMemo, useCallback, useState, useRef, PropsWithChildren } from 'react';
import { useLocalStorage } from '@mantine/hooks';
import { useLocation } from 'react-router-dom';
import { LEAGUE } from '../models/NBA';
import { VideoEvent } from '../client';

export interface LeagueSearchSnapshot {
  searchParamsString: string;
  videos: VideoEvent[];
  total: number;
  cursor: string | null;
  hasMore: boolean;
}

export interface LeagueContextType {
  league: LEAGUE;
  setLeague: (league: LEAGUE) => void;
  searchCache: Record<LEAGUE, LeagueSearchSnapshot | null>;
  saveSearchSnapshot: (league: LEAGUE, snapshot: LeagueSearchSnapshot) => void;
  clearSearchSnapshot: (league: LEAGUE) => void;
  getSearchUrl: (targetLeague: LEAGUE) => string;
  mergeSearchResults: (
    targetLeague: LEAGUE,
    searchParamsString: string,
    items: VideoEvent[],
    nextCursor: string | null,
    total: number | null,
    isInitialPage: boolean,
    limit?: number
  ) => void;
}

const LeagueContext = createContext<LeagueContextType | undefined>(undefined);

const parseLeagueParam = (search: string): LEAGUE | null => {
  const param = new URLSearchParams(search).get('league')?.toLowerCase();
  if (param === 'wnba') return LEAGUE.WNBA;
  if (param === 'nba') return LEAGUE.NBA;
  return null;
};

export const LeagueProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const paramLeague = location.pathname === '/search' ? parseLeagueParam(location.search) : null;

  const [storedLeague, setStoredLeague] = useLocalStorage<LEAGUE>({
    key: 'nba-insights-league',
    defaultValue: LEAGUE.NBA,
    getInitialValueInEffect: false,
  });

  const lastParamLeagueRef = useRef<LEAGUE | null>(paramLeague);
  const [activeLeague, setActiveLeague] = useState<LEAGUE>(paramLeague || storedLeague);

  const league = activeLeague;

  const [searchCache, setSearchCache] = useState<Record<LEAGUE, LeagueSearchSnapshot | null>>({
    [LEAGUE.NBA]: null,
    [LEAGUE.WNBA]: null,
  });

  const saveSearchSnapshot = useCallback((targetLeague: LEAGUE, snapshot: LeagueSearchSnapshot) => {
    setSearchCache((prev) => ({
      ...prev,
      [targetLeague]: snapshot,
    }));
  }, []);

  const clearSearchSnapshot = useCallback((targetLeague: LEAGUE) => {
    setSearchCache((prev) => ({
      ...prev,
      [targetLeague]: null,
    }));
  }, []);

  const setLeague = useCallback(
    (newLeague: LEAGUE) => {
      lastParamLeagueRef.current = newLeague;
      setActiveLeague(newLeague);
      setStoredLeague(newLeague);
    },
    [setStoredLeague]
  );

  // Sync when URL on /search has a valid league param changed externally (e.g. browser back/forward)
  useEffect(() => {
    if (paramLeague && paramLeague !== lastParamLeagueRef.current) {
      lastParamLeagueRef.current = paramLeague;
      setActiveLeague(paramLeague);
      setStoredLeague(paramLeague);
    }
  }, [paramLeague, setStoredLeague]);

  useEffect(() => {
    if (!paramLeague && storedLeague && storedLeague !== activeLeague) {
      setActiveLeague(storedLeague);
    }
  }, [paramLeague, storedLeague, activeLeague]);

  useEffect(() => {
    document.title = league === LEAGUE.WNBA ? 'WNBA Insights' : 'NBA Insights';
    document.documentElement.setAttribute('data-league', league);
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [league]);

  const getSearchUrl = useCallback(
    (targetLeague: LEAGUE): string => {
      const cached = searchCache[targetLeague];
      if (cached && cached.searchParamsString) {
        const query = cached.searchParamsString.startsWith('?')
          ? cached.searchParamsString.slice(1)
          : cached.searchParamsString;
        return `/search?${query}`;
      }
      return `/search?league=${targetLeague}`;
    },
    [searchCache]
  );

  const mergeSearchResults = useCallback(
    (
      targetLeague: LEAGUE,
      searchParamsString: string,
      items: VideoEvent[],
      nextCursor: string | null,
      total: number | null,
      isInitialPage: boolean,
      limit: number = 15
    ) => {
      setSearchCache((prev) => {
        const prevVideos = isInitialPage ? [] : (prev[targetLeague]?.videos ?? []);
        const existing = new Set(prevVideos.map((v) => `${v.gameId}-${v.eventNumber}`));
        const newItems = items.filter((item) => !existing.has(`${item.gameId}-${item.eventNumber}`));
        const combinedVideos = [...prevVideos, ...newItems];

        return {
          ...prev,
          [targetLeague]: {
            searchParamsString,
            videos: combinedVideos,
            total: total ?? prev[targetLeague]?.total ?? combinedVideos.length,
            cursor: nextCursor,
            hasMore: Boolean(nextCursor && items.length >= limit),
          },
        };
      });
    },
    []
  );

  const contextValue = useMemo(
    () => ({
      league,
      setLeague,
      searchCache,
      saveSearchSnapshot,
      clearSearchSnapshot,
      getSearchUrl,
      mergeSearchResults,
    }),
    [league, setLeague, searchCache, saveSearchSnapshot, clearSearchSnapshot, getSearchUrl, mergeSearchResults]
  );

  return (
    <LeagueContext.Provider value={contextValue}>
      <div data-league={league} style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {children}
      </div>
    </LeagueContext.Provider>
  );
};

export const useLeague = (): LeagueContextType => {
  const context = useContext(LeagueContext);
  if (!context) {
    throw new Error('useLeague must be used within a LeagueProvider');
  }
  return context;
};
